import type { CSSProperties, ReactNode } from 'react';

// Imperative toast bridge for SHARED (both-shell) modules. Each shell registers
// its impl at module load: App.tsx the sonner one, AppV1.tsx the antd statics
// (src/utils/antdApNotify.ts — dies with the v1 tree at R15). antd itself is
// deliberately NOT imported here (IK1, 7.3): this file is in the v4 boot graph.

export type ApMessageOpenArgs = {
  type?: 'success' | 'error' | 'info' | 'warning' | 'loading';
  content: ReactNode;
  key?: string | number;
  duration?: number;
  style?: CSSProperties;
};

export type ApNotificationArgs = {
  message: ReactNode;
  description?: ReactNode;
  key?: string;
  duration?: number; // antd semantics: seconds; 0 = sticky
  // antd-only presentation extras (UIHelper.showNotification configs) — passed
  // through verbatim by the antd impl, ignored by the sonner impl.
  icon?: ReactNode;
  top?: number;
  className?: string;
};

export type ApNotifyImpl = {
  message: {
    success: (content: ReactNode) => void;
    error: (content: ReactNode) => void;
    info: (content: ReactNode) => void;
    warning: (content: ReactNode) => void;
    loading: (content: ReactNode) => void;
    open: (args: ApMessageOpenArgs) => void;
    destroy: (key?: string | number) => void;
  };
  notification: {
    success: (args: ApNotificationArgs) => void;
    error: (args: ApNotificationArgs) => void;
    info: (args: ApNotificationArgs) => void;
    warning: (args: ApNotificationArgs) => void;
    destroy: (key?: string) => void;
  };
};

// Unregistered = no-op: both shells register at module load, before any UI
// event can fire a toast.
let impl: ApNotifyImpl | null = null;

export const registerApNotify = (shellImpl: ApNotifyImpl): void => {
  impl = shellImpl;
};

export const message: ApNotifyImpl['message'] = {
  success: (content) => void impl?.message.success(content),
  error: (content) => void impl?.message.error(content),
  info: (content) => void impl?.message.info(content),
  warning: (content) => void impl?.message.warning(content),
  loading: (content) => void impl?.message.loading(content),
  open: (args) => void impl?.message.open(args),
  destroy: (key) => void impl?.message.destroy(key),
};

export const notification: ApNotifyImpl['notification'] = {
  success: (args) => void impl?.notification.success(args),
  error: (args) => void impl?.notification.error(args),
  info: (args) => void impl?.notification.info(args),
  warning: (args) => void impl?.notification.warning(args),
  destroy: (key) => void impl?.notification.destroy(key),
};

// UIHelper.showNotification compat surface (SE1, 7.3b): same call shape the v1
// helper always had; 'destroy' routes by key.
export const showNotification = (
  type: 'success' | 'error' | 'info' | 'warning' | 'destroy',
  config: any
): void => {
  if (type === 'destroy') {
    notification.destroy(config);
    return;
  }
  notification[type](config);
};
